/**
 * 工具站顯示清單
 * Super Admin 可勾選要在工具站首頁顯示哪些工具；儲存至 system_settings.tool_station_visible_slugs
 */

import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { get, patch } from '../../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import toast from 'react-hot-toast';

/** 與工具站 apps/tool 的 TOOLS 對齊（slug + name） */
const TOOL_STATION_TOOLS: { slug: string; name: string }[] = [
  { slug: 'qr-generator', name: 'QR 產生器' },
  { slug: 'image-editor', name: '圖片修改器' },
  { slug: 'ecommerce-editor', name: '電商編輯器' },
  { slug: 'unit-converter', name: '單位換算' },
  { slug: 'badminton-team', name: '羽球自動編隊對戰' },
  { slug: 'regex-tester', name: 'Regex 可視化測試器' },
  { slug: 'html-preview', name: 'HTML 可視化網頁' },
  { slug: 'compound-loan-calculator', name: '複利・房貸計算器' },
  { slug: 'short-url', name: '短網址生成器' },
  { slug: 'line-message-preview', name: 'LINE 訊息預覽器' },
  { slug: 'pdf-tools', name: 'PDF 轉檔與壓縮' },
  { slug: 'weekend-flight-radar', name: '週末快閃機票搜尋器' },
  { slug: 'lcc-fee-calculator', name: '廉航手續費計算器' },
  { slug: 'hotel-coupon-list', name: '訂房折扣碼彙整' },
  { slug: 'fast-food-coupon-search', name: '速食優惠代碼快搜' },
  { slug: 'ai-drizzle', name: '智能 Drizzle 產生器' },
  { slug: 'ai-speech-to-text', name: '語音轉文字' },
  { slug: 'ai-text', name: 'AI 文字小幫手' },
];

const SETTING_KEY = 'tool_station_visible_slugs';

interface SystemSettingListResponse {
  success: boolean;
  data: Array<{
    id: string;
    key: string;
    value: Record<string, unknown>;
    description: string | null;
    updatedAt: string;
  }>;
}

const ALL_SLUGS = new Set(TOOL_STATION_TOOLS.map(t => t.slug));

export function ToolStationVisible() {
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(
    new Set(ALL_SLUGS)
  );
  const [isSaving, setIsSaving] = useState(false);

  const { data, error, isLoading, mutate } = useSWR<SystemSettingListResponse>(
    '/api/super-admin/system-settings',
    get as (url: string) => Promise<SystemSettingListResponse>
  );

  const setting = data?.data?.find(s => s.key === SETTING_KEY);
  const savedSlugs = Array.isArray(
    (setting?.value as { slugs?: string[] })?.slugs
  )
    ? ((setting?.value as { slugs: string[] }).slugs as string[])
    : null;

  useEffect(() => {
    if (isLoading) return;
    if (savedSlugs === null) {
      setSelectedSlugs(new Set(ALL_SLUGS));
      return;
    }
    setSelectedSlugs(
      new Set(savedSlugs.length > 0 ? savedSlugs : Array.from(ALL_SLUGS))
    );
  }, [savedSlugs, isLoading]);

  const toggle = (slug: string) => {
    setSelectedSlugs(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const selectAll = () => setSelectedSlugs(new Set(ALL_SLUGS));
  const clearAll = () => setSelectedSlugs(new Set());

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await patch(`/api/super-admin/system-settings/${SETTING_KEY}`, {
        value: { slugs: Array.from(selectedSlugs) },
        description: '工具站要顯示的工具 slug 列表，空陣列表示全部顯示',
      });
      toast.success('已儲存');
      mutate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '儲存失敗');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout title="工具站顯示">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          勾選要在工具站首頁顯示的工具；未勾選的工具不會出現在導航與首頁卡片。空清單表示全部顯示。
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={selectAll}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            全選
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            全不選
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-lg bg-primary px-4 py-1.5 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {isSaving ? '儲存中…' : '儲存'}
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <ErrorDisplay error={error} onRetry={() => mutate()} />
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {TOOL_STATION_TOOLS.map(t => (
              <li
                key={t.slug}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3"
              >
                <input
                  type="checkbox"
                  id={`tool-${t.slug}`}
                  checked={selectedSlugs.has(t.slug)}
                  onChange={() => toggle(t.slug)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label
                  htmlFor={`tool-${t.slug}`}
                  className="cursor-pointer text-sm font-medium text-gray-900"
                >
                  {t.name}
                </label>
                <span className="ml-1 text-xs text-gray-400">({t.slug})</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
